import { createFileRoute } from '@tanstack/react-router';
import { useMutation, useAction, useQuery } from 'convex/react';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Card } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useEffect, useState } from 'react';
import { Mail, CheckCircle2, AlertCircle, Clock, Copy, Code, Send } from 'lucide-react';
import { toast } from '@template/lib/toast';
import { useTranslation } from 'react-i18next';
import { formatBerlinTime } from '@/lib/berlin-time';

interface EmailTest {
  id: string;
  name: string;
  description: string;
  type: 'payment-reminder' | 'application-reminder' | 'system-test';
  execute: () => Promise<void>;
  inputFields?: { name: string; label: string; placeholder: string }[];
}

const EmailTestingPage = () => {
  const { t, i18n } = useTranslation('operator');
  const [loading, setLoading] = useState<string | null>(null);
  const [results, setResults] = useState<Array<{ id: string; status: 'success' | 'error'; message: string; timestamp: number }>>([]);
  const [inputs, setInputs] = useState<Record<string, string>>({});

  const templateList = useQuery(api.app.emailTemplates.listEmailTemplates);
  const templates = templateList ?? [];
  const isTemplateListLoading = templateList === undefined;

  const [selectedTemplateId, setSelectedTemplateId] = useState<string>('');
  const [templatePayload, setTemplatePayload] = useState<string>('');
  const [customRecipient, setCustomRecipient] = useState<string>('');
  const [templateLoading, setTemplateLoading] = useState(false);

  const sendPaymentReminder = useMutation(api.app.billing.sendPaymentReminder);
  const sendTestEmail = useAction(api.app.emailActions.sendTestEmail);
  const sendTemplatePreview = useAction(api.app.emailActions.sendTemplatePreview);

  const selectedTemplate = templates.find((template: any) => template.id === selectedTemplateId);

  useEffect(() => {
    if (!templateList || templateList.length === 0) {
      return;
    }

    const hasSelected = templateList.some((template: any) => template.id === selectedTemplateId);

    if (!hasSelected) {
      const [firstTemplate] = templateList;
      setSelectedTemplateId(firstTemplate.id);
      setTemplatePayload(firstTemplate.examplePayload);
    }
  }, [templateList, selectedTemplateId]);

  const emailTests: EmailTest[] = [
    {
      id: 'payment-reminder',
      name: t('emailTesting.tests.paymentReminder.name'),
      description: t('emailTesting.tests.paymentReminder.description'),
      type: 'payment-reminder',
      inputFields: [
        {
          name: 'invoiceId',
          label: t('emailTesting.tests.paymentReminder.fields.invoiceIdLabel'),
          placeholder: t('emailTesting.tests.paymentReminder.fields.invoiceIdPlaceholder'),
        },
      ],
      execute: async () => {
        const invoiceId = inputs['payment-reminder-invoiceId'];
        if (!invoiceId || invoiceId.trim() === '') {
          throw new Error(t('emailTesting.tests.paymentReminder.errors.invoiceIdRequired'));
        }
        await sendPaymentReminder({
          workInvoiceId: invoiceId as any,
        });
      },
    },
    {
      id: 'system-test',
      name: t('emailTesting.tests.systemTest.name'),
      description: t('emailTesting.tests.systemTest.description'),
      type: 'system-test',
      execute: async () => {
        await sendTestEmail();
        toast.success(t('emailTesting.toasts.systemTestQueued'));
      },
    },
  ];

  const handleExecuteTest = async (test: EmailTest) => {
    setLoading(test.id);
    try {
      await test.execute();
      const result = {
        id: test.id,
        status: 'success' as const,
        message: t('emailTesting.results.queuedSuccess', { name: test.name }),
        timestamp: Date.now(),
      };
      setResults((prev) => [result, ...prev]);
      toast.success(result.message);
    } catch (error: any) {
      const result = {
        id: test.id,
        status: 'error' as const,
        message: t('emailTesting.results.queuedError', { name: test.name, error: error.message }),
        timestamp: Date.now(),
      };
      setResults((prev) => [result, ...prev]);
      toast.error(result.message);
    } finally {
      setLoading(null);
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    toast.success(t('emailTesting.toasts.copied'));
  };

  const handleSelectTemplate = (templateId: string) => {
    setSelectedTemplateId(templateId);
    const template = templates.find((item: any) => item.id === templateId);
    setTemplatePayload(template?.examplePayload ?? '');
  };

  const handleSendTemplate = async () => {
    if (!selectedTemplateId) {
      toast.error(t('emailTesting.toasts.templateRequired'));
      return;
    }

    if (!templatePayload.trim()) {
      toast.error(t('emailTesting.toasts.payloadRequired'));
      return;
    }

    try {
      JSON.parse(templatePayload);
    } catch (error: any) {
      toast.error(t('emailTesting.toasts.payloadInvalid', { error: error instanceof Error ? error.message : t('emailTesting.common.unknownError') }));
      return;
    }

    setTemplateLoading(true);

    try {
      const trimmedRecipient = customRecipient.trim();
      const result = await sendTemplatePreview({
        templateId: selectedTemplateId,
        variables: templatePayload,
        ...(trimmedRecipient && { destination: trimmedRecipient }),
      });

      const messageBase = result.success
        ? t('emailTesting.results.templateQueued', { templateId: result.templateId, subject: 'subject' in result && result.subject ? ` – ${result.subject}` : '' })
        : t('emailTesting.results.templateFailed', { templateId: result.templateId, error: 'error' in result && result.error ? `: ${result.error}` : '' });

      setResults((prev) => [
        {
          id: `${result.templateId}-${Date.now()}`,
          status: result.success ? 'success' : 'error',
          message: messageBase,
          timestamp: Date.now(),
        },
        ...prev,
      ]);

      if (result.success) {
        toast.success(messageBase);
      } else {
        toast.error(messageBase);
      }
    } catch (error: any) {
      const errorMessage = error instanceof Error ? error.message : t('emailTesting.common.unknownTemplateError');
      const message = t('emailTesting.results.templateFailedWithMessage', { templateId: selectedTemplateId, error: errorMessage });

      setResults((prev) => [
        {
          id: `${selectedTemplateId}-${Date.now()}`,
          status: 'error',
          message,
          timestamp: Date.now(),
        },
        ...prev,
      ]);

      toast.error(message);
    } finally {
      setTemplateLoading(false);
    }
  };

  return (
    <div className='p-6 max-w-6xl mx-auto space-y-6'>
      {/* Header */}
      <div className='space-y-2'>
        <div className='flex items-center gap-2'>
          <Mail className='w-8 h-8 text-primary' />
          <h1 className='text-3xl font-bold'>{t('emailTesting.title')}</h1>
        </div>
        <div className='text-muted-foreground'>
          {t('emailTesting.descriptionPrefix')} <Badge variant='outline'>{t('emailTesting.mailtrapInbox')}</Badge> {t('emailTesting.descriptionSuffix')}
        </div>
      </div>

      {/* Mailtrap Info */}
      <Alert>
        <Mail className='h-4 w-4' />
        <AlertDescription>
          {t('emailTesting.mailtrapInfo.prefix')}{' '}
          <a href='https://mailtrap.io' target='_blank' rel='noopener noreferrer' className='underline font-semibold'>
            mailtrap.io
          </a>{' '}
          {t('emailTesting.mailtrapInfo.suffix')}
        </AlertDescription>
      </Alert>

      {/* Email Templates */}
      <div className='space-y-4'>
        <h2 className='text-xl font-semibold'>{t('emailTesting.availableTemplates')}</h2>

        {emailTests.map((test) => (
          <Card key={test.id} className='p-6'>
            <div className='space-y-4'>
              {/* Header */}
              <div className='flex items-start justify-between'>
                <div>
                  <h3 className='text-lg font-semibold'>{test.name}</h3>
                  <p className='text-sm text-muted-foreground'>{test.description}</p>
                </div>
                <Badge variant='secondary'>{test.type}</Badge>
              </div>

              {/* Input Fields */}
              {test.inputFields && (
                <div className='space-y-3 bg-muted/30 p-4 rounded-lg'>
                  {test.inputFields.map((field) => {
                    const inputId = `${test.id}-${field.name}`;
                    return (
                      <div key={field.name}>
                        <label htmlFor={inputId} className='text-sm font-medium block mb-1'>
                          {field.label}
                        </label>
                        <Input
                          id={inputId}
                          placeholder={field.placeholder}
                          value={inputs[inputId] || ''}
                          onChange={(e) =>
                            setInputs((prev) => ({
                              ...prev,
                              [inputId]: e.target.value,
                            }))
                          }
                        />
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Action Button */}
              <div className='flex justify-end'>
                <Button onClick={() => handleExecuteTest(test)} disabled={loading === test.id} className='gap-2'>
                  {loading === test.id ? (
                    <>
                      <Clock className='w-4 h-4 animate-spin' />
                      {t('emailTesting.actions.sending')}
                    </>
                  ) : (
                    <>
                      <Mail className='w-4 h-4' />
                      {t('emailTesting.actions.sendTestEmail')}
                    </>
                  )}
                </Button>
              </div>
            </div>
          </Card>
        ))}
      </div>

      {/* Custom Template Trigger */}
      <Card className='p-6'>
        <div className='space-y-6'>
          <div className='flex flex-col gap-2 md:flex-row md:items-start md:justify-between'>
            <div className='space-y-2'>
              <div className='flex items-center gap-2'>
                <Code className='w-5 h-5 text-primary' />
                <h3 className='text-lg font-semibold'>{t('emailTesting.customTemplate.title')}</h3>
              </div>
              <p className='text-sm text-muted-foreground'>{t('emailTesting.customTemplate.description')}</p>
            </div>
            <div className='flex items-center gap-2'>
              {selectedTemplate && <Badge variant='outline'>{selectedTemplate.id}</Badge>}
              <Badge variant='secondary'>{t('emailTesting.customTemplate.advancedBadge')}</Badge>
            </div>
          </div>

          <div className='grid gap-4 md:grid-cols-2'>
            <div className='space-y-2'>
              <label id='template-label' className='text-sm font-medium'>
                {t('emailTesting.customTemplate.templateLabel')}
              </label>
              <Select
                value={selectedTemplateId}
                onValueChange={(v) => {
                  if (v !== null) handleSelectTemplate(v);
                }}
                disabled={isTemplateListLoading}
              >
                <SelectTrigger aria-labelledby='template-label'>
                  <SelectValue placeholder={isTemplateListLoading ? t('emailTesting.customTemplate.loadingTemplates') : t('emailTesting.customTemplate.selectTemplate')} />
                </SelectTrigger>
                <SelectContent>
                  {templates.map((template: any) => (
                    <SelectItem key={template.id} value={template.id}>
                      {template.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {selectedTemplate && <p className='text-xs text-muted-foreground leading-relaxed'>{selectedTemplate.description}</p>}
            </div>

            <div className='space-y-2'>
              <label htmlFor='recipient-override' className='text-sm font-medium'>
                {t('emailTesting.customTemplate.optionalRecipient')}
              </label>
              <Input id='recipient-override' placeholder={t('emailTesting.customTemplate.recipientPlaceholder')} value={customRecipient} onChange={(event) => setCustomRecipient(event.target.value)} />
              <p className='text-xs text-muted-foreground leading-relaxed'>{t('emailTesting.customTemplate.recipientHelp')}</p>
            </div>
          </div>

          {selectedTemplate && selectedTemplate.fields.length > 0 && (
            <div className='rounded-lg border border-dashed border-muted bg-muted/30 p-4'>
              <p className='text-sm font-semibold mb-3'>{t('emailTesting.customTemplate.variables')}</p>
              <div className='grid gap-3 md:grid-cols-2'>
                {selectedTemplate.fields.map((field: any) => (
                  <div key={field.key} className='space-y-1'>
                    <div className='flex items-center gap-2'>
                      <Badge variant={field.required ? 'default' : 'outline'}>{field.required ? t('emailTesting.common.required') : t('emailTesting.common.optional')}</Badge>
                      <span className='text-sm font-medium text-foreground'>{field.label}</span>
                    </div>
                    <p className='text-xs text-muted-foreground leading-relaxed'>{field.description || field.key}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className='space-y-2'>
            <label htmlFor='variables-json' className='text-sm font-medium'>
              {t('emailTesting.customTemplate.variablesJson')}
            </label>
            <Textarea
              id='variables-json'
              value={templatePayload}
              onChange={(event) => setTemplatePayload(event.target.value)}
              className='min-h-[240px] font-mono text-xs'
              spellCheck={false}
              placeholder={selectedTemplate ? selectedTemplate.examplePayload : '{ }'}
            />
            <p className='text-xs text-muted-foreground leading-relaxed'>{t('emailTesting.customTemplate.variablesHelp')}</p>
          </div>

          <div className='flex justify-end'>
            <Button onClick={handleSendTemplate} disabled={templateLoading || !selectedTemplateId} className='gap-2'>
              {templateLoading ? (
                <>
                  <Clock className='w-4 h-4 animate-spin' />
                  {t('emailTesting.actions.sending')}
                </>
              ) : (
                <>
                  <Send className='w-4 h-4' />
                  {t('emailTesting.actions.sendTemplate')}
                </>
              )}
            </Button>
          </div>
        </div>
      </Card>

      {/* Test Results */}
      {results.length > 0 && (
        <div className='space-y-4'>
          <h2 className='text-xl font-semibold'>{t('emailTesting.testResults')}</h2>

          <div className='space-y-2'>
            {results.map((result, index) => (
              <Card key={index} className={`p-4 flex items-start justify-between ${result.status === 'success' ? 'bg-success/10 border-success/20' : 'bg-destructive/10 border-destructive/20'}`} role={result.status === 'error' ? 'alert' : undefined}>
                <div className='flex items-start gap-3'>
                  {result.status === 'success' ? <CheckCircle2 className='w-5 h-5 text-success mt-0.5' aria-hidden='true' /> : <AlertCircle className='w-5 h-5 text-destructive mt-0.5' aria-hidden='true' />}
                  <div>
                    <p className={result.status === 'success' ? 'text-success' : 'text-destructive'}>{result.message}</p>
                    <p className='text-xs text-muted-foreground mt-1'>{formatBerlinTime(result.timestamp, i18n.language || 'de-DE')}</p>
                  </div>
                </div>
                <button onClick={() => copyToClipboard(result.message)} className='text-muted-foreground hover:text-foreground' aria-label={t('emailTesting.copyResultAria')}>
                  <Copy className='w-4 h-4' aria-hidden='true' />
                </button>
              </Card>
            ))}
          </div>
        </div>
      )}

      {/* Debug Info */}
      <Card className='p-4 bg-muted/30'>
        <h3 className='font-semibold mb-2'>{t('emailTesting.debug.title')}</h3>
        <div className='space-y-1 text-sm font-mono'>
          <div>
            <span className='text-muted-foreground'>{t('emailTesting.debug.environmentLabel')}</span> <Badge variant='outline'>{import.meta.env.DEV ? t('emailTesting.debug.environmentDev') : t('emailTesting.debug.environmentProd')}</Badge>
          </div>
          <div>
            <span className='text-muted-foreground'>{t('emailTesting.debug.findInvoiceIdsLabel')}</span> <span className='text-foreground'>{t('emailTesting.debug.findInvoiceIdsValue')}</span>
          </div>
        </div>
      </Card>
    </div>
  );
};

export const Route = createFileRoute('/_protected/operators/emails-test')({
  component: EmailTestingPage,
});
